// Typed ts-rest client. Built against the shared contract package.
// Always sends cookies so the session middleware on the server can mint/read
// the `septima-session` cookie.
//
// Base URL selection (single source of truth — `baseUrl` below):
//   - Local dev: VITE_API_BASE is empty, so baseUrl === '' and every request
//     is relative (`/api/...`). The Vite dev server's same-origin proxy
//     forwards /api to the Nest server (see vite.config.ts).
//   - Production (S3/CloudFront SPA → ALB API): VITE_API_BASE is the absolute
//     API origin (e.g. https://api.septima.example.com), baked in at BUILD
//     time. Requests then go cross-origin to that host.
// Because cross-origin requests carry cookies (`credentials: 'include'`), the
// server must set the session cookie `SameSite=None; Secure` in production so
// the browser will send it back to the API origin (the server already does).

import { initClient } from '@ts-rest/core';
import { apiContract } from '@septima/contract';
import type { StaffActionResult } from '@septima/contract';
import { analyticsSessionId, setLastRequestId, trackError } from '@/lib/analytics/client';

const baseUrl = (import.meta.env.VITE_API_BASE as string | undefined) ?? '';

// Grouping id only — never identity (the server re-derives actor/persona from
// the session). Lets the observability layer correlate every API call of a
// single browser visit; the value is the stable per-visit sessionStorage id.
export const api = initClient(apiContract, {
    baseUrl,
    baseHeaders: { 'X-Session-Id': analyticsSessionId() },
    credentials: 'include',
});

// Bridge wrapper around the typed `{status, body}` envelope returned by
// per-area ts-rest calls. Normalises into the `StaffActionResult` shape the
// pages and the typed-dispatcher helpers consume — a single
// `if (result.ok) ...` glue point for every mutation that supersedes the
// legacy named-action surface (OLD_CODE.html #2).
//
// Treats any non-200 status as `{ok: false, error: <message>}`; preserves
// the typed 200 body verbatim. Endpoint 4xx bodies (`{ok: false, error}`)
// flow through directly.
type EnvelopeStatus = { status: number; body: unknown; headers?: Headers };

/**
 * The human-readable reason a mutation failed.
 *
 * A Nest exception body puts the useful text in `message` and the bare HTTP
 * reason phrase in `error` (`{message: 'Action "create-relationship"
 * precondition failed: CLIENT_ALREADY_OWNED: …', error: 'Bad Request'}`), so
 * reading `error` alone showed staff "Bad Request" for every governed Action
 * refusal — the reason was dropped exactly where it mattered. Prefer `message`,
 * fall back to the app's own `{ok: false, error}` envelope, then the status.
 */
export function staffErrorMessage(body: unknown, status: number): string {
    const record = body && typeof body === 'object' ? (body as Record<string, unknown>) : undefined;
    const message = record?.message;
    if (typeof message === 'string' && message.trim() !== '') return message;
    // A ValidationPipe rejection carries an array of issue strings.
    if (Array.isArray(message)) {
        const joined = message
            .filter((entry): entry is string => typeof entry === 'string')
            .join('; ');
        if (joined.trim() !== '') return joined;
    }
    const error = record?.error;
    if (typeof error === 'string' && error.trim() !== '') return error;
    return `Request failed (status ${status})`;
}

export async function runStaffEndpoint(call: Promise<EnvelopeStatus>): Promise<StaffActionResult> {
    const res = await call;
    // Capture the server's X-Request-Id so a subsequent frontend `error` row
    // shares its correlation id (PRD #345 / ADR-0021, WS4). Defensive: ts-rest
    // may or may not surface response headers.
    try {
        setLastRequestId(res.headers?.get?.('x-request-id') ?? null);
    } catch {
        /* ignore */
    }
    const body = res.body as Record<string, unknown> | undefined;
    if (res.status === 200) {
        return (body ?? { ok: true }) as StaffActionResult;
    }
    const err = staffErrorMessage(body, res.status);
    // A failed mutation is a frontend `error` event (routed to trace_events).
    trackError({ name: 'ApiError', message: err, status: res.status });
    return { ok: false, error: err };
}

// Raw-fetch escape hatch for endpoints the ts-rest client doesn't cover
// (Outlook OAuth, auth/session). Mirrors the baseUrl + credentials behaviour
// of the typed client so callers don't have to remember either.
export function apiUrl(path: string): string {
    if (!path.startsWith('/')) return path;
    return `${baseUrl}${path}`;
}

export async function apiFetch(path: string, init?: RequestInit): Promise<Response> {
    const res = await window.fetch(apiUrl(path), {
        credentials: 'include',
        ...init,
        // Same visit-grouping id as the typed client; merged after `init` so a
        // caller's own headers still ride along.
        headers: { 'X-Session-Id': analyticsSessionId(), ...init?.headers },
    });
    try {
        setLastRequestId(res.headers.get('x-request-id'));
    } catch {
        /* ignore */
    }
    return res;
}
